import { useState } from 'react';
import { Link } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { ORIGIN_LABELS, type TicketDetail } from '@erp/shared';
import {
  ChevronDown,
  Clock,
  Mail,
  Pencil,
  Phone,
  Star,
  UserRound,
  Users,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input, Label } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { cn, formatDateTime, formatMinutes } from '@/lib/utils';
import { equipmentListOptions } from '@/features/resources/resources-queries';
import {
  deskDetailQuery,
  selectableTicketGroups,
  ticketGroupsQuery,
  ticketsListQuery,
} from './tickets-queries';
import { usePatchTicket } from './tickets-mutations';
import { errorMessage, initials, isTicketMirrored, isTicketReadOnly } from './ticket-format';
import { TicketStatusChip } from './detail-status-chip';
import { TicketEntitiesCard } from './detail-entities';
import { SlaCard } from './detail-sla-card';
import { AttachmentsSection } from './detail-attachments';
import { TicketRatingCard } from './detail-rating';
import { TicketDispatchBlock } from './ticket-dispatch-block';
import { DescriptionCard } from './description-editor';
import { TicketSheetCard, useSheetProfile } from './ticket-sheet-card';
import { SgrRecursoField } from '@/features/sgr/SgrRecursoField';
import { useSgr } from '@/features/sgr/sgr-queries';
import { CatalogItemPicker, useCatalogTrees, useDeskCatalogs } from './CatalogItemPicker';
import { findCatalogPath } from './ticket-sla';
import { TechnicianPicker } from '@/features/service-orders/TechnicianPicker';
import { TeamMemberPicker } from '@/features/team/TeamMemberPicker';

/** Aba "Informações gerais": grid 3 colunas (solicitante | descrição | ticket). */
export function InfoGeneralTab({
  ticket,
  onChangeClient,
}: {
  ticket: TicketDetail;
  onChangeClient: () => void;
}) {
  return (
    <div className="grid items-start gap-4 lg:grid-cols-[300px_minmax(0,1fr)] xl:grid-cols-[300px_minmax(0,1fr)_340px]">
      <div className="min-w-0 space-y-4">
        <RequestorCard ticket={ticket} onChangeClient={onChangeClient} />
        <LastTicketsCard clientId={ticket.client.id} currentTicketNumber={ticket.ticketNumber} />
      </div>

      <div className="min-w-0 space-y-4">
        {/* a ficha do associado (spec 031): campos de verdade, com a origem de
            cada valor; só nos perfis com ficha — no genérico não aparece */}
        <TicketSheetCard ticket={ticket} />
        {/* editável depois de criado (pedido da PoolTrack, 03/10) — o HTML do
            TiFlux continua sanitizado em DescriptionText */}
        <DescriptionCard ticket={ticket} />
        <AttachmentsSection
          ticketNumber={ticket.ticketNumber}
          readOnly={isTicketReadOnly(ticket)}
        />
        <TicketEntitiesCard
          ticketNumber={ticket.ticketNumber}
          readOnly={isTicketMirrored(ticket)}
        />
      </div>

      <div className="min-w-0 space-y-4 lg:col-span-2 xl:col-span-1">
        <TicketDispatchBlock ticketNumber={ticket.ticketNumber} />
        <SlaCard ticket={ticket} />
        <TicketRatingCard ticketNumber={ticket.ticketNumber} />
        <TicketInfoCard ticket={ticket} />
      </div>
    </div>
  );
}

// ── Coluna esquerda ─────────────────────────────────────────────────────────

export function RequestorCard({
  ticket,
  onChangeClient,
}: {
  ticket: TicketDetail;
  onChangeClient: () => void;
}) {
  const patch = usePatchTicket(ticket.ticketNumber);
  // editar o solicitante e trocar o cliente valem até em ticket fechado, mas
  // não em espelhado (spec 011): lá o dono é o TiFlux
  const mirrored = isTicketMirrored(ticket);
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');

  const startEdit = () => {
    setName(ticket.requestorName ?? '');
    setPhone(ticket.requestorPhone ?? '');
    setEmail(ticket.requestorEmail ?? '');
    setEditing(true);
  };

  const save = () => {
    patch.mutate(
      {
        requestorName: name.trim() || null,
        requestorPhone: phone.trim() || null,
        requestorEmail: email.trim() || null,
      },
      { onSuccess: () => setEditing(false) }
    );
  };

  return (
    <section className="rounded-lg border bg-card p-4 text-card-foreground shadow-sm">
      <h2 className="mb-3 text-base font-semibold">Informações do solicitante</h2>

      <div className="mb-1 flex items-center justify-between">
        <h3 className="text-sm font-semibold">Solicitante</h3>
        {!editing && !mirrored ? (
          <Button
            variant="ghost"
            size="icon-xs"
            className="text-muted-foreground"
            title="Editar solicitante"
            onClick={startEdit}
          >
            <Pencil />
          </Button>
        ) : null}
      </div>

      {editing ? (
        <div className="space-y-2">
          <div className="space-y-1">
            <Label htmlFor="req-name" className="text-xs">
              Nome
            </Label>
            <Input
              id="req-name"
              className="h-8"
              value={name}
              onChange={(e) => setName(e.target.value)}
              autoFocus
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="req-phone" className="text-xs">
              Telefone
            </Label>
            <Input
              id="req-phone"
              type="tel"
              className="h-8"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="req-email" className="text-xs">
              E-mail
            </Label>
            <Input
              id="req-email"
              type="email"
              className="h-8"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>
          {patch.isError ? (
            <p className="text-sm text-destructive">{errorMessage(patch.error)}</p>
          ) : null}
          <div className="flex justify-end gap-2 pt-1">
            <Button
              variant="outline"
              size="sm"
              disabled={patch.isPending}
              onClick={() => setEditing(false)}
            >
              Cancelar
            </Button>
            <Button size="sm" disabled={patch.isPending} onClick={save}>
              Salvar
            </Button>
          </div>
        </div>
      ) : (
        <div className="space-y-2 text-sm">
          <IconRow icon={<UserRound className="size-4" />} value={ticket.requestorName} />
          <IconRow icon={<Phone className="size-4" />} value={ticket.requestorPhone} />
          <IconRow icon={<Mail className="size-4" />} value={ticket.requestorEmail} />
        </div>
      )}

      <div className="mt-4 border-t pt-3">
        <h3 className="mb-1 text-sm font-semibold">Cliente</h3>
        <div className="flex items-center gap-2 text-sm">
          <Users className="size-4 shrink-0 text-muted-foreground" />
          <Link
            to="/clients/$clientId"
            params={{ clientId: String(ticket.client.id) }}
            className="min-w-0 truncate font-medium hover:text-primary hover:underline"
            title={ticket.client.name}
          >
            {ticket.client.name}
          </Link>
        </div>
        {!mirrored ? (
          <button
            type="button"
            onClick={onChangeClient}
            className="mt-2 text-sm font-medium text-primary hover:underline"
          >
            Trocar cliente
          </button>
        ) : null}
      </div>
    </section>
  );
}

function IconRow({ icon, value }: { icon: React.ReactNode; value: string | null }) {
  return (
    <div className="flex items-center gap-2">
      <span className="shrink-0 text-muted-foreground">{icon}</span>
      <span className={cn('min-w-0 truncate', !value && 'text-muted-foreground')}>
        {value || '—'}
      </span>
    </div>
  );
}

/** Card colapsável "Últimos tickets" do cliente (exclui o ticket atual). */
function LastTicketsCard({
  clientId,
  currentTicketNumber,
}: {
  clientId: number;
  currentTicketNumber: number;
}) {
  const [open, setOpen] = useState(true);
  const listQ = useQuery({
    ...ticketsListQuery({
      client_ids: [clientId],
      limit: 8,
      sort: 'updated_at',
      order: 'desc',
    }),
    enabled: open,
  });

  const others = (listQ.data?.items ?? []).filter(
    (t) => t.ticketNumber !== currentTicketNumber
  );

  return (
    <Collapsible
      open={open}
      onOpenChange={setOpen}
      className="rounded-lg border bg-card text-card-foreground shadow-sm"
    >
      <CollapsibleTrigger className="flex w-full items-center justify-between gap-2 p-4 text-left">
        <h2 className="text-base font-semibold">Últimos tickets</h2>
        <ChevronDown
          className={cn(
            'size-4 shrink-0 text-muted-foreground transition-transform',
            open && 'rotate-180'
          )}
        />
      </CollapsibleTrigger>
      <CollapsibleContent className="px-4 pb-4">
        {listQ.isPending ? (
          <div className="space-y-2">
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-4/5" />
            <Skeleton className="h-4 w-full" />
          </div>
        ) : listQ.isError ? (
          <p className="text-sm text-muted-foreground">Erro ao carregar tickets do cliente.</p>
        ) : others.length === 0 ? (
          <p className="text-sm text-muted-foreground">Nenhum outro ticket deste cliente.</p>
        ) : (
          <ul className="divide-y">
            {others.slice(0, 6).map((t) => (
              <li key={t.ticketNumber} className="py-2 first:pt-0 last:pb-0">
                <div className="flex items-center gap-2">
                  <Link
                    to="/tickets/$ticketNumber"
                    params={{ ticketNumber: String(t.ticketNumber) }}
                    className="shrink-0 text-sm font-medium text-primary hover:underline"
                  >
                    #{t.ticketNumber}
                  </Link>
                  <span className="min-w-0 flex-1 truncate text-sm" title={t.title}>
                    {t.title}
                  </span>
                  <TicketStatusChip kind={t.status.kind} name={t.status.name} />
                </div>
              </li>
            ))}
          </ul>
        )}
      </CollapsibleContent>
    </Collapsible>
  );
}

// ── Coluna direita ──────────────────────────────────────────────────────────

export function TicketInfoCard({ ticket }: { ticket: TicketDetail }) {
  const patch = usePatchTicket(ticket.ticketNumber);
  const deskQ = useQuery(deskDetailQuery(ticket.desk.id));
  const deskCatalogsQ = useDeskCatalogs(ticket.desk.id);
  const equipmentsQ = useQuery(
    equipmentListOptions({ client_ids: [ticket.client.id], active: true, limit: 200 })
  );
  const groupsQ = useQuery(ticketGroupsQuery());
  // spec 032: onde a integração do SGR é deste tenant, o Recurso é a busca pela placa
  const sheetProfile = useSheetProfile();
  const sgr = useSgr();
  const sgrRecurso = sheetProfile && sgr.available;

  // fechado/cancelado OU espelhado (spec 011): os campos viram texto
  const readOnly = isTicketReadOnly(ticket);
  // o técnico se corrige também em ticket fechado (como o solicitante), mas
  // nunca no espelhado: lá o dono é o TiFlux e a API recusa (409)
  const mirrored = isTicketMirrored(ticket);
  const pending = patch.isPending;

  const stages = deskQ.data ? [...deskQ.data.stages].sort((a, b) => a.position - b.position) : [];
  const priorities = deskQ.data
    ? [...deskQ.data.priorities].sort((a, b) => a.position - b.position)
    : [];
  const equipments = equipmentsQ.data?.items ?? [];
  // Grupos ativos + o do ticket (segue selecionável mesmo se inativado depois).
  const groups = selectableTicketGroups(groupsQ.data ?? [], ticket.group?.id);

  return (
    <section className="rounded-lg border bg-card p-4 text-card-foreground shadow-sm">
      <h2 className="mb-4 text-base font-semibold">Informações do ticket</h2>

      <div className="space-y-4">
        <InfoField label="Mesa de serviço">
          <p className="text-sm">{ticket.desk.name}</p>
        </InfoField>

        <InfoField label="Prioridade">
          {readOnly ? (
            <p className="text-sm">{ticket.priority?.name ?? 'Sem prioridade'}</p>
          ) : (
            <Select
              value={ticket.priority ? String(ticket.priority.id) : 'none'}
              disabled={pending || !deskQ.data}
              onValueChange={(v) =>
                patch.mutate({ priorityId: v === 'none' ? null : Number(v) })
              }
            >
              <SelectTrigger size="sm" className="w-full">
                <SelectValue placeholder="Sem prioridade" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Sem prioridade</SelectItem>
                {ticket.priority && !priorities.some((p) => p.id === ticket.priority?.id) ? (
                  <SelectItem value={String(ticket.priority.id)}>
                    {ticket.priority.name}
                  </SelectItem>
                ) : null}
                {priorities.map((p) => (
                  <SelectItem key={p.id} value={String(p.id)}>
                    {p.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </InfoField>

        {(deskCatalogsQ.data?.length ?? 0) > 0 || ticket.catalogItemId !== null ? (
          <InfoField label="Item de catálogo">
            {readOnly ? (
              <CatalogItemPathText deskId={ticket.desk.id} itemId={ticket.catalogItemId} />
            ) : (
              <CatalogItemPicker
                deskId={ticket.desk.id}
                value={ticket.catalogItemId}
                disabled={pending}
                onChange={(catalogItemId) => patch.mutate({ catalogItemId })}
              />
            )}
          </InfoField>
        ) : null}

        <InfoField label="Status">
          <TicketStatusChip kind={ticket.status.kind} name={ticket.status.name} />
        </InfoField>

        <InfoField label="Estágio">
          {readOnly ? (
            <p className="text-sm">{ticket.stage.name}</p>
          ) : (
            <Select
              value={String(ticket.stage.id)}
              disabled={pending || !deskQ.data}
              onValueChange={(v) => patch.mutate({ stageId: Number(v) })}
            >
              <SelectTrigger size="sm" className="w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {stages.some((s) => s.id === ticket.stage.id) ? null : (
                  <SelectItem value={String(ticket.stage.id)}>{ticket.stage.name}</SelectItem>
                )}
                {stages.map((s) => (
                  <SelectItem key={s.id} value={String(s.id)}>
                    {s.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </InfoField>

        {/* Só a equipe deste tenant, com a UX do técnico (pedido da PoolTrack,
            05/10): a lista era GET /users, com os logins de portal das
            associações e cortada em 200 por nome */}
        <InfoField label="Responsável">
          {readOnly ? (
            <ResponsibleValue name={ticket.responsible?.name ?? null} />
          ) : (
            <TeamMemberPicker
              value={ticket.responsible}
              disabled={pending}
              onChange={(responsibleId) => patch.mutate({ responsibleId })}
            />
          )}
        </InfoField>

        <InfoField label="Técnico responsável">
          {mirrored ? (
            <p className={cn('text-sm', !ticket.technician && 'text-muted-foreground')}>
              {ticket.technician?.name ?? 'Sem técnico'}
            </p>
          ) : (
            <TechnicianPicker
              value={ticket.technician}
              disabled={pending}
              onChange={(technicianId) => patch.mutate({ technicianId })}
            />
          )}
        </InfoField>

        <InfoField label="Recurso">
          {!readOnly && sgrRecurso ? (
            <SgrRecursoField ticket={ticket} />
          ) : readOnly ? (
            <p className={cn('text-sm', !ticket.equipment && 'text-muted-foreground')}>
              {ticket.equipment?.name ?? 'Sem recurso'}
            </p>
          ) : (
            <Select
              value={ticket.equipment ? String(ticket.equipment.id) : 'none'}
              disabled={pending || equipmentsQ.isPending}
              onValueChange={(v) =>
                patch.mutate({ equipmentId: v === 'none' ? null : Number(v) })
              }
            >
              <SelectTrigger size="sm" className="w-full">
                <SelectValue placeholder="Sem recurso" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">
                  <span className="text-muted-foreground">Sem recurso</span>
                </SelectItem>
                {ticket.equipment && !equipments.some((e) => e.id === ticket.equipment?.id) ? (
                  <SelectItem value={String(ticket.equipment.id)}>
                    {ticket.equipment.name}
                  </SelectItem>
                ) : null}
                {equipments.map((e) => (
                  <SelectItem key={e.id} value={String(e.id)}>
                    {e.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </InfoField>

        {/* v11: grupo de tickets — só aparece quando há grupos selecionáveis
            (o do ticket incluso, mesmo inativo, via selectableTicketGroups). */}
        {groups.length > 0 ? (
          <InfoField label="Grupo">
            {readOnly ? (
              <p className={cn('text-sm', !ticket.group && 'text-muted-foreground')}>
                {ticket.group?.name ?? 'Sem grupo'}
              </p>
            ) : (
              <Select
                value={ticket.group ? String(ticket.group.id) : 'none'}
                disabled={pending || groupsQ.isPending}
                onValueChange={(v) =>
                  patch.mutate({ groupId: v === 'none' ? null : Number(v) })
                }
              >
                <SelectTrigger size="sm" className="w-full">
                  <SelectValue placeholder="Sem grupo" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">
                    <span className="text-muted-foreground">Sem grupo</span>
                  </SelectItem>
                  {groups.map((g) => (
                    <SelectItem key={g.id} value={String(g.id)}>
                      {g.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </InfoField>
        ) : null}

        {patch.isError ? (
          <p className="text-sm text-destructive">{errorMessage(patch.error)}</p>
        ) : null}

        <div className="grid grid-cols-2 gap-x-3 gap-y-4 border-t pt-4">
          <InfoField label="Origem">
            <div className="flex items-center gap-1.5 text-sm">
              <Star className="size-3.5 shrink-0 text-muted-foreground" />
              {ORIGIN_LABELS[ticket.createdVia]}
            </div>
          </InfoField>
          <InfoField label="Criado em">
            <div className="flex items-center gap-1.5 text-sm">
              <Clock className="size-3.5 shrink-0 text-muted-foreground" />
              {formatDateTime(ticket.createdAt)}
            </div>
          </InfoField>
          {ticket.closedAt ? (
            <InfoField label="Fechado em">
              <p className="text-sm">{formatDateTime(ticket.closedAt)}</p>
            </InfoField>
          ) : null}
          {ticket.canceledAt ? (
            <InfoField label="Cancelado em">
              <p className="text-sm">{formatDateTime(ticket.canceledAt)}</p>
            </InfoField>
          ) : null}
          <InfoField label="Horas gastas">
            <p className="text-sm tabular-nums">{formatMinutes(ticket.workedMinutes)}</p>
          </InfoField>
        </div>
      </div>
    </section>
  );
}

/** Texto "Catálogo › Área › Item" (tickets fechados/cancelados, sem edição). */
function CatalogItemPathText({ deskId, itemId }: { deskId: number; itemId: number | null }) {
  const catalogsQ = useDeskCatalogs(itemId !== null ? deskId : undefined);
  const trees = useCatalogTrees((catalogsQ.data ?? []).map((c) => c.id));
  if (itemId === null) return <p className="text-sm text-muted-foreground">Sem item</p>;
  const path = findCatalogPath(trees, itemId);
  if (!path) return <p className="text-sm text-muted-foreground">Item #{itemId}</p>;
  return (
    <p className="text-sm" title={`${path.catalog.name} › ${path.area.name} › ${path.item.name}`}>
      <span className="text-muted-foreground">
        {path.catalog.name} › {path.area.name} ›{' '}
      </span>
      {path.item.name}
    </p>
  );
}

function InfoField({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="space-y-1">
      <div className="text-sm font-semibold">{label}</div>
      {children}
    </div>
  );
}

function ResponsibleValue({ name }: { name: string | null }) {
  if (!name) return <span className="text-sm text-muted-foreground">Sem responsável</span>;
  return (
    <span className="flex min-w-0 items-center gap-2">
      <Avatar size="sm">
        <AvatarFallback className="bg-primary/15 text-[10px] font-semibold text-primary">
          {initials(name)}
        </AvatarFallback>
      </Avatar>
      <span className="min-w-0 truncate text-sm">{name}</span>
    </span>
  );
}
